// Shared building blocks for the landing-page section renderers: the text-size
// scale, highlighted headline words, star ratings, avatar stacks and the proof
// line. SectionRenderer owns the per-section switch; the v2.58 funnel blocks
// (nav, banner, testimonials, modules, bio, offer) live in their own files and
// receive a `SectionKit` so every block shares one set of click semantics,
// inline-edit hooks and style tokens.
import type { ReactNode } from 'react';
import type { MarkStyle, PageTheme, Section, SectionTextSize } from '../../model/pages';
import { readableOn } from '../../model/pages';
import type { StyleClasses } from '../styles';

// Per-element class strings for the four text sizes. 'md' is EXACTLY what each
// element rendered before text_size existed — a section without text_size (every
// old page and published bundle) keeps a byte-identical class attribute.
// Scale: sm ≈ 0.85×, lg ≈ 1.25×, xl ≈ 1.6× in Tailwind steps.
export const TEXT_SIZE = {
  hero_headline: {
    sm: 'text-3xl leading-[1.1] sm:text-4xl', md: 'text-4xl leading-[1.1] sm:text-5xl',
    lg: 'text-5xl leading-[1.1] sm:text-6xl', xl: 'text-6xl leading-[1.1] sm:text-7xl',
  },
  hero_sub: {
    sm: 'text-sm leading-relaxed sm:text-base', md: 'text-base leading-relaxed sm:text-lg',
    lg: 'text-lg leading-relaxed sm:text-xl', xl: 'text-xl leading-relaxed sm:text-2xl',
  },
  eyebrow: { sm: 'text-[11px]', md: 'text-xs', lg: 'text-sm', xl: 'text-base' },
  heading: { sm: 'text-xl sm:text-2xl', md: 'text-2xl sm:text-3xl', lg: 'text-3xl sm:text-4xl', xl: 'text-4xl sm:text-5xl' },
  body: { sm: 'text-sm leading-relaxed', md: 'leading-relaxed', lg: 'text-lg leading-relaxed', xl: 'text-xl leading-relaxed' },
  small: { sm: 'text-xs leading-relaxed', md: 'text-sm leading-relaxed', lg: 'text-base leading-relaxed', xl: 'text-lg leading-relaxed' },
  title: { sm: 'text-sm font-semibold', md: 'font-semibold', lg: 'text-lg font-semibold', xl: 'text-xl font-semibold' },
  quote: { sm: 'text-base leading-relaxed', md: 'text-lg leading-relaxed', lg: 'text-xl leading-relaxed', xl: 'text-2xl leading-relaxed' },
  guarantee_heading: { sm: 'text-lg', md: 'text-xl', lg: 'text-2xl', xl: 'text-3xl' },
  stat_value: {
    sm: 'text-2xl font-extrabold sm:text-3xl', md: 'text-3xl font-extrabold sm:text-4xl',
    lg: 'text-4xl font-extrabold sm:text-5xl', xl: 'text-5xl font-extrabold sm:text-6xl',
  },
  stat_label: { sm: 'text-xs', md: 'text-sm', lg: 'text-base', xl: 'text-lg' },
  faq_q: { sm: 'text-sm font-medium', md: 'font-medium', lg: 'text-lg font-medium', xl: 'text-xl font-medium' },
  price: { sm: 'text-2xl font-extrabold', md: 'text-3xl font-extrabold', lg: 'text-4xl font-extrabold', xl: 'text-5xl font-extrabold' },
  cta: { sm: 'text-sm', md: '', lg: 'text-lg', xl: 'text-xl' },
} satisfies Record<string, Record<SectionTextSize, string>>;
export type TextKey = keyof typeof TEXT_SIZE;

/** Display scale for the modern (roomy) styles: larger, tighter headlines and a
 *  more readable body. Only these keys differ; every other key (and every
 *  classic style) uses TEXT_SIZE unchanged. */
export const ROOMY_SIZE = {
  hero_headline: {
    sm: 'text-[2.15rem] leading-[1.08] sm:text-5xl',
    md: 'text-[2.6rem] leading-[1.05] sm:text-6xl',
    lg: 'text-[2.85rem] leading-[1.03] sm:text-7xl',
    xl: 'text-[3.1rem] leading-[1.0] sm:text-[5.25rem]',
  },
  heading: {
    sm: 'text-2xl leading-[1.15] sm:text-3xl',
    md: 'text-[1.85rem] leading-[1.12] sm:text-[2.5rem]',
    lg: 'text-[2.1rem] leading-[1.08] sm:text-5xl',
    xl: 'text-[2.5rem] leading-[1.04] sm:text-6xl',
  },
  hero_sub: {
    sm: 'text-base leading-relaxed sm:text-lg',
    md: 'text-[17px] leading-relaxed sm:text-xl',
    lg: 'text-lg leading-relaxed sm:text-[1.375rem]',
    xl: 'text-xl leading-relaxed sm:text-2xl',
  },
  body: { sm: 'text-[15px] leading-relaxed', md: 'text-[17px] leading-[1.65]', lg: 'text-lg leading-[1.7]', xl: 'text-xl leading-[1.7]' },
} satisfies Partial<Record<TextKey, Record<SectionTextSize, string>>>;

/** Split `text` around each `|`-separated phrase of `highlight` (first
 *  occurrence of each, case-sensitive, in reading order). Pure + unit-tested.
 *  Returns null when nothing matches, so callers render the raw string. */
export function splitHighlight(text: string, highlight: string | undefined): { t: string; hl: boolean }[] | null {
  const phrases = (highlight ?? '').split('|').map((p) => p.trim()).filter(Boolean);
  if (!text || phrases.length === 0) return null;
  const hits: { at: number; len: number }[] = [];
  for (const p of phrases) {
    const at = text.indexOf(p);
    if (at >= 0 && !hits.some((h) => at < h.at + h.len && h.at < at + p.length)) hits.push({ at, len: p.length });
  }
  if (hits.length === 0) return null;
  hits.sort((a, b) => a.at - b.at);
  const out: { t: string; hl: boolean }[] = [];
  let i = 0;
  for (const h of hits) {
    if (h.at > i) out.push({ t: text.slice(i, h.at), hl: false });
    out.push({ t: text.slice(h.at, h.at + h.len), hl: true });
    i = h.at + h.len;
  }
  if (i < text.length) out.push({ t: text.slice(i), hl: false });
  return out;
}

/** Inline style + class for one highlighted run. `color` = the mark fill (the
 *  style's markColor, else the brand). */
export function markProps(mark: MarkStyle, color: string, accent: string): { className: string; style: React.CSSProperties } {
  switch (mark) {
    case 'marker':
      // A highlighter swipe under the lower 60% of the letters.
      return {
        className: 'px-[0.08em] [box-decoration-break:clone] [-webkit-box-decoration-break:clone]',
        style: { backgroundImage: `linear-gradient(transparent 52%, ${color} 52%, ${color} 92%, transparent 92%)` },
      };
    case 'box':
      return {
        className: 'rounded-[0.14em] px-[0.16em] [box-decoration-break:clone] [-webkit-box-decoration-break:clone]',
        style: { background: color, color: readableOn(color) },
      };
    case 'italic':
      return { className: 'italic', style: { fontWeight: 'inherit' } };
    case 'underline':
      return {
        className: 'underline decoration-[0.08em] underline-offset-[0.16em] [text-decoration-skip-ink:none]',
        style: { textDecorationColor: color },
      };
    case 'color':
    default:
      // The style's markColor when it sets one (e.g. a gold that reads on
      // white), else the accent.
      return { className: '', style: { color: color || accent } };
  }
}

/** Render `text` with its highlighted phrases marked. Plain string when there
 *  is no highlight (or no match), so unhighlighted copy renders exactly as before. */
export function Marked({ text, highlight, mark, color, accent }: {
  text: string; highlight?: string; mark: MarkStyle; color: string; accent: string;
}): ReactNode {
  const parts = splitHighlight(text, highlight);
  if (!parts) return text;
  const p = markProps(mark, color, accent);
  return parts.map((r, i) => (r.hl
    ? <span key={i} className={p.className} style={p.style}>{r.t}</span>
    : <span key={i}>{r.t}</span>));
}

/** A row of 1-5 filled stars (fractional ratings round to the nearest star). */
export function Stars({ n = 5, color = '#f5b301', size = 16, className = '' }: { n?: number; color?: string; size?: number; className?: string }) {
  const count = Math.max(0, Math.min(5, Math.round(n)));
  if (!count) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} role="img" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: count }, (_, i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true"><path fill={color} d="m12 2.6 2.9 6 6.5.9-4.7 4.6 1.1 6.5L12 17.5l-5.8 3.1 1.1-6.5L2.6 9.5l6.5-.9L12 2.6z" /></svg>
      ))}
    </span>
  );
}

/** Overlapping circular avatars (social-proof stack). `ring` = the surface color
 *  behind them so each avatar reads as cut out of the page. */
export function AvatarStack({ urls, size = 36, ring = '#ffffff' }: { urls: string[]; size?: number; ring?: string }) {
  const list = urls.filter(Boolean).slice(0, 6);
  if (!list.length) return null;
  return (
    <span className="flex items-center" aria-hidden>
      {list.map((u, i) => (
        <img key={i} src={u} alt="" loading="lazy" decoding="async"
          className="rounded-full object-cover" width={size} height={size}
          style={{ width: size, height: size, marginLeft: i ? -size * 0.28 : 0, boxShadow: `0 0 0 2px ${ring}` }} />
      ))}
    </span>
  );
}

/** Editor-canvas placeholder for an empty text field, keyed by the section field
 *  its edits write. Anything not listed reads "Add text". */
const FIELD_PLACEHOLDER: Record<string, string> = {
  eyebrow: 'Add a label', headline: 'Add a headline', subheadline: 'Add a subheadline', heading: 'Add a heading',
  body: 'Add text', label: 'Button text', cta_label: 'Button text', cta2_label: 'Add a link',
  quote: 'Add a quote', attribution: 'Add a name', role: 'Add a role', cta_note: 'Add a note', proof_text: 'Add a line',
  price: 'Add a price', price_compare: 'Add a price', price_note: 'Add a note',
  items: 'Add an item', left_items: 'Add an item', right_items: 'Add an item', left_title: 'Add a title', right_title: 'Add a title',
};

/** The placeholder for the field a text patch writes, e.g. `(t) => ({ headline: t })`
 *  → "Add a headline". Pure. */
export function fieldPlaceholder(patch: (text: string) => Partial<Section>): string {
  return FIELD_PLACEHOLDER[Object.keys(patch(''))[0] ?? ''] ?? 'Add text';
}

/** Everything a section block needs from SectionRenderer. */
export interface SectionKit {
  section: Section;
  s: StyleClasses;
  theme: PageTheme;
  /** Accent for buttons + accents (the brand color; the fill's ink inside a brand band). */
  brand: string;
  /** Legible text on top of `brand`. */
  onBrand: string;
  /** Button fill (theme.button_color, else the brand) and its readable text. */
  btnBg: string;
  onBtn: string;
  tz: (k: TextKey) => string;
  /** Raw text on public pages; an inline editor on the selected canvas section.
   *  On the canvas an empty value shows `placeholder` (default: by field). */
  txt: (value: string, patch: (text: string) => Partial<Section>, multiline?: boolean, placeholder?: string) => ReactNode;
  /** Heading text with the section's highlight applied (inline editor while selected). */
  hl: (value: string, patch: (text: string) => Partial<Section>, placeholder?: string) => ReactNode;
  /** The optional centered section header (eyebrow + heading + subheadline). */
  head: ReactNode | null;
  withHead: (node: ReactNode) => ReactNode;
  /** A button/link with the page's click semantics (#next, popup #form, new tab). */
  link: (label: string, href: string, className: string, style?: React.CSSProperties, children?: ReactNode) => ReactNode;
  /** Editor-only placeholder for a block with no content yet (null on public pages). */
  ph: (title: string, hint: string) => ReactNode;
  /** Small eyebrow label in the style's treatment. */
  eyebrowCls: string;
  stack?: boolean;
  /** Removes a block's preset measure when its section has an explicit width. */
  contentStyle?: React.CSSProperties;
  /** This section is selected on the editor canvas (its text is inline-editable). */
  editing: boolean;
  /** Rendering on the editor canvas at all (selected or not). Empty fields that
   *  must stay clickable render a placeholder only when this is set. */
  editable: boolean;
}
